'use client';

import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';

export function usePackStorage() {
  const [purchasedPacks, setPurchasedPacks] = useState([]);
  const [freePacks, setFreePacks] = useState([]);
  const [purchasedFreePacksCollective, setPurchasedFreePacksCollective] = useState([]);
  const [selectedPack, setSelectedPack] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  const loadFromStorage = () => {
    try {
      const storedPacks = localStorage.getItem('purchasedPacks');
      const storedSelectedPack = localStorage.getItem('selectedPack');

      if (storedPacks) {
        const packs = JSON.parse(storedPacks);
        setPurchasedPacks(packs);
      }

      if (storedSelectedPack) {
        const selected = JSON.parse(storedSelectedPack);
        setSelectedPack(selected);
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
  };

  const saveToStorage = (packs, selected, isFree = false) => {
    try {
      localStorage.setItem(`${isFree ? 'freePacks' : 'purchasedPacks'}`, JSON.stringify(packs));
      if (selected) {
        localStorage.setItem('selectedPack', JSON.stringify(selected));
        localStorage.setItem('selectedPackId', selected._id);
      }
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const handlePackChange = (packId) => {
    if (packId === 'view-other-tests') {
      window.open(
        'https://www.quizrr.in/#packs?utm_source=ayjr_jm_26&utm_medium=ayjr_platform&utm_campaign=ayjr',
        '_blank',
      );
      return;
    }

    const selectedPackData = purchasedFreePacksCollective.find((pack) => pack._id === packId);
    if (selectedPackData) {
      setSelectedPack(selectedPackData);
      saveToStorage(purchasedPacks, selectedPackData);

      window.dispatchEvent(
        new CustomEvent('packChanged', {
          detail: { selectedPack: selectedPackData, purchasedFreePacksCollective },
        }),
      );

      setTimeout(() => {
        const currentPath = location.pathname;

        if (currentPath.includes('/v3/') && currentPath.includes('/tests')) {
          const pathParts = currentPath.split('/');
          const v3Index = pathParts.indexOf('v3');

          if (v3Index !== -1 && pathParts[v3Index + 1]) {
            const currentPackId = pathParts[v3Index + 1];
            if (currentPackId !== packId) {
              pathParts[v3Index + 1] = packId;
              const newPath = pathParts.join('/');

              navigate(newPath, { replace: true });
            }
          }
        } else if (currentPath.includes('/v3/') && currentPath.includes('/resources')) {
          const pathParts = currentPath.split('/');
          const v3Index = pathParts.indexOf('v3');

          if (v3Index !== -1 && pathParts[v3Index + 1]) {
            const currentPackId = pathParts[v3Index + 1];
            if (currentPackId !== packId) {
              pathParts[v3Index + 1] = packId;
              const newPath = pathParts.join('/');
              navigate(newPath, { replace: true });
            }
          }
        } else if (currentPath.includes('/v2/') && currentPath.includes('/analysis')) {
          const pathParts = currentPath.split('/');
          const v2Index = pathParts.indexOf('v2');

          if (v2Index !== -1 && pathParts[v2Index + 1]) {
            const currentPackId = pathParts[v2Index + 1];

            if (currentPackId !== packId) {
              pathParts[v2Index + 1] = packId;
              const newPath = pathParts.join('/');

              navigate(newPath, { replace: true });
            }
          }
        }
      }, 100);
    }
  };

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'selectedPack' || e.key === 'purchasedPacks') {
        loadFromStorage();
      }
    };

    const handlePackChangeEvent = (e) => {
      const { selectedPack: newSelectedPack, purchasedPacks: newPurchasedPacks } = e.detail;
      setSelectedPack(newSelectedPack);
      setPurchasedPacks(newPurchasedPacks);
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('packChanged', handlePackChangeEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('packChanged', handlePackChangeEvent);
    };
  }, []);

  useEffect(() => {
    const fetchPacks = async () => {
      const apiUrl = `/api/packs/v3`;

      try {
        setIsLoading(true);
        loadFromStorage();

        const response = await axios.get(apiUrl);
        const packs = response?.data?.data?.purchasedPacks || [];
        const freePacksRes = response?.data?.data?.freePacks || [];
        setPurchasedPacks(packs);
        setFreePacks(freePacksRes);
        setPurchasedFreePacksCollective([...packs, ...freePacksRes]);

        if (packs.length > 0) {
          const savedPackId = localStorage.getItem('selectedPackId');
          let packToSelect = packs[0];

          if (savedPackId) {
            const savedPack = packs.find((pack) => pack._id === savedPackId);
            if (savedPack) {
              packToSelect = savedPack;
            }
          }

          setSelectedPack(packToSelect);
          saveToStorage(packs, packToSelect);
        } else if (freePacksRes.length > 0) {
          setSelectedPack(freePacksRes[0]);
          saveToStorage(freePacksRes, freePacksRes[0], true);
        }
      } catch (error) {
        console.error('Error fetching packs:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchPacks();
  }, []);

  return {
    purchasedPacks,
    freePacks,
    purchasedFreePacksCollective,
    selectedPack,
    isLoading,
    handlePackChange,
  };
}
